import { ArrowRight, Play } from "lucide-react";
import { parseEndDate, useTimeRemaining } from "./campaign-time-remaining";

import { Button } from "../ui/button";
import { CampaignImage } from "./campaign-image";
import type { CampaignSchema } from "@/types";
import type { HomeCardLayout } from "@/campaigns";
import type { ReactNode } from "react";
import { Typography } from "../ui/typography";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import { campaignKey } from "@/campaigns/campaign-key";
import { cn } from "@/lib/utils";
import { selectJoined } from "@/stores/campaigns.store";
import { useAction } from "@/hooks/use-action";
import { useAppStore } from "@/stores";
import { useTranslation } from "react-i18next";
import { PrizeText } from "@/components/prize-text";
import { CampaignLink } from "@/components/campaign-link";

/**
 * The simplified card the mixed home feed renders for every campaign type.
 * Deliberately light — type badge, title, ONE hook line, meta + one CTA; live
 * detail (position, score, full prize tiers) belongs to the detail view. The
 * type-specific strings (badge, hook, stats) come from the owning module's
 * `homeCard`, so this stays type-agnostic.
 */
export interface CompactCampaignCardProps {
  campaign: CampaignSchema;
  /** Type badge text, e.g. "Tournament" / "Prize drop". */
  typeLabel: string;
  /** Type badge icon (module-owned), e.g. a Trophy / Gift lucide icon. */
  typeIcon?: ReactNode;
  /** The one hook — top prize / top drop. Hook row is hidden when null. */
  hookValue: ReactNode | null;
  hookLabel: string;
  /** The hook prize is a catalog item: star mark, never a currency symbol from its title. */
  hookIsCatalogItem?: boolean;
  /** Optional footer cue on the grid card, e.g. "18 prizes left". */
  metaExtra?: ReactNode;
  layout: HomeCardLayout;
}

/** Card treatment when the campaign holds an unrevealed win: a gold border.
 *  Static by design — a pulsing ring here animates a large, image-heavy card
 *  in a multi-card feed and janks the scroll. */
const WON_CARD_RING = "border-prizes/70";

/** "You have won!" flag for a card with an unrevealed prize — mirrors
 *  TypeBadge's corner-flush design in the prizes colour, opposite corner. */
const WonBadge = ({ featured }: { featured?: boolean }) => {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "absolute top-0 right-0 z-20 rounded-bl-md bg-prizes px-2 py-1",
        // Featured insets the image, so match its rounding at the outer corner.
        featured && "rounded-tr-(--border-radius-inner)"
      )}
    >
      <Typography tag="h6" className="font-bold text-background">
        {t("igwButton.youHaveWon", "You have won!")}
      </Typography>
    </div>
  );
};

/** Mirrors CampaignDurationBadge's design: corner-flush, inner-corner radius,
 *  bg-background/70, muted icon + h6 label — same family, opposite corner. */
const TypeBadge = ({ label, icon }: { label: string; icon?: ReactNode }) => (
  <div className="absolute top-0 left-0 z-20 flex w-auto items-center gap-1 rounded-br-md bg-background/70 px-2 py-1">
    {icon && (
      <span className="flex size-4 items-center justify-center text-muted-foreground [&_svg]:size-4">
        {icon}
      </span>
    )}
    <Typography tag="h6">{label}</Typography>
  </div>
);

/** Opt in / Joined / nothing — "NONE" opt-in campaigns get no button at all.
 *  One CTA label for every campaign type: `campaign.optIn` ("Opt In"), the same
 *  key the detail-view opt-in and the overlay use. */
const CardCta = ({
  campaign,
  className,
  featured,
}: {
  campaign: CampaignSchema;
  className?: string;
  /** Featured CTA is taller with bigger type and an arrow (Figma 5406:702). */
  featured?: boolean;
}) => {
  const { t } = useTranslation();
  const joined = useAppStore((s) => selectJoined(s, campaignKey(campaign)));
  const { optIn } = useAction();

  if (joined) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-full border border-border bg-background px-4 py-1.5",
          featured && "py-3",
          className
        )}
      >
        <Typography tag="h6" className="text-muted-foreground">
          {t("campaign.joined", "Joined")}
        </Typography>
      </div>
    );
  }

  if (campaign.opt_in_method === "NONE") return null;

  return (
    <Button
      variant="optin"
      size={featured ? "default" : "sm"}
      className={cn(
        featured && "h-auto py-3.5 text-xl font-bold [&_svg]:size-5",
        className
      )}
      onClick={(e) => {
        // The card body is a link to the detail view — keep the join tap local.
        e.preventDefault();
        e.stopPropagation();
        optIn(campaign);
      }}
    >
      {t("campaign.optIn", "Opt In")} {featured ? <ArrowRight /> : <Play />}
    </Button>
  );
};

/**
 * Label stacked ABOVE the value (the featured card's pattern), never beside it:
 * inline, the two competed for one row and the PRIZE was the side that
 * truncated ("25 Free Spi… NEXT REWARD"). Prize titles are priority content and
 * must render whole (see CLAUDE.md) — stacked, the value gets the full card
 * width, shrinks, then glides on one line (`fit="marquee"`) when it still
 * doesn't fit — every card keeps the same height; the label is an overline instead of
 * being replaced by an icon, which would need overflow measurement and lose its
 * meaning across locales.
 */
const HookLine = ({
  value,
  label,
  isCatalogItem,
}: {
  value: ReactNode;
  label: string;
  isCatalogItem?: boolean;
}) => (
  <div className="flex min-w-0 flex-col">
    <Typography
      tag="h6"
      className="text-[10px] leading-4 tracking-wider text-muted-foreground uppercase"
    >
      {label}
    </Typography>
    <PrizeText
      size="base"
      weight="bold"
      icon
      currencyMark={!isCatalogItem}
      fit="marquee"
      role="heading"
      aria-level={4}
      className="w-full"
    >
      {value}
    </PrizeText>
  </div>
);

export const CompactCampaignCard = ({
  campaign,
  typeLabel,
  typeIcon,
  hookValue,
  hookLabel,
  hookIsCatalogItem,
  metaExtra,
  layout,
}: CompactCampaignCardProps) => {
  const { t } = useTranslation();
  const id = campaignKey(campaign);
  const featured = layout === "featured";
  // Type-agnostic: any campaign type's card flags its unrevealed win the same
  // way (the seam is shared), so this lives here rather than in a module.
  const hasWin = useAppStore((s) => id in s.unrevealedWins);
  // Only the featured layout renders the "Ends in …" line — the grid card gets
  // its ticker from the badge on the artwork instead.
  const remaining = useTimeRemaining(parseEndDate(campaign.end), t, featured);

  const image = (
    <div className={cn("relative", featured && "lg:w-[55%] lg:shrink-0")}>
      {hasWin && <WonBadge featured={featured} />}
      {/* Full-bleed: the card's rounded-xl + overflow-hidden clips the corners.
          The 2:1 art is never cropped (partners bake text into it) — the CARD's
          max-width caps how tall the image can get instead (see below). */}
      <CampaignImage
        campaign={campaign}
        hideTitle
        hideDuration={featured}
        className={cn(
          featured
            ? // Featured: inset with its own rounding (Figma 5406:693).
              "rounded-(--border-radius-inner) lg:h-full"
            : "rounded-none"
        )}
      />
      {/* Bottom scrim keeps the corner badges readable on busy artwork.
          Featured shows type + time in the content column instead. */}
      {!featured && (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-12 bg-gradient-to-t from-background/70 to-transparent" />
          <TypeBadge label={typeLabel} icon={typeIcon} />
          {/* Meta cue (e.g. "18 prizes left") rides the image like the duration
              badge — opposite corner, same design family — costing no card height. */}
          {metaExtra != null && (
            <div className="absolute bottom-0 left-0 z-20 rounded-tr-md bg-background/70 px-2 py-1">
              <Typography tag="h6" className="text-muted-foreground">
                {metaExtra}
              </Typography>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (featured) {
    // Figma 5406:692 — padded card, inset image; content (top → bottom):
    // type pill, title, TOP PRIZE label over a 48px prizes-gold value,
    // "Ends in …", full-width CTA.
    return (
      <CampaignLink campaignId={id} className="group w-full">
        <div
          className={cn(
            "flex h-full w-full flex-col gap-5 rounded-(--border-radius) border border-border bg-secondary p-[18px] lg:flex-row lg:items-center lg:gap-7",
            "transition-colors duration-200",
            hasWin ? WON_CARD_RING : "group-hover:border-primary/40"
          )}
        >
          {image}
          <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-4">
            <div className="rounded-full bg-background-card-surface px-2.5 py-1">
              <Typography
                tag="h6"
                className="text-xs font-semibold text-muted-foreground uppercase"
              >
                {typeLabel}
              </Typography>
            </div>

            {/* leading keeps truncate from clipping descenders at this size. */}
            <Typography
              tag="h3"
              className="w-full truncate text-[28px] leading-[1.3]"
            >
              {campaignDisplayTitle(campaign, t)}
            </Typography>

            {hookValue != null && (
              <div className="flex w-full min-w-0 flex-col">
                <Typography
                  tag="h6"
                  className="text-sm font-semibold tracking-[0.06em] text-muted-foreground uppercase"
                >
                  {hookLabel}
                </Typography>
                {/* No truncate: prize titles render whole (CLAUDE.md) — a long
                    one shrinks, then glides on its one line instead. */}
                <PrizeText
                  size="xl"
                  icon
                  currencyMark={!hookIsCatalogItem}
                  fit="marquee"
                  role="heading"
                  aria-level={2}
                  className="w-full"
                >
                  {hookValue}
                </PrizeText>
              </div>
            )}

            <div className="flex items-center gap-1 font-body text-sm text-muted-foreground">
              {t("campaign.endsInShort", "Ends in")} {remaining}
            </div>

            <CardCta campaign={campaign} featured className="w-full" />
          </div>
        </div>
      </CampaignLink>
    );
  }

  return (
    <CampaignLink campaignId={id} className="group w-full">
      <div
        className={cn(
          "flex h-full w-full flex-col overflow-hidden rounded-xl border border-border bg-secondary",
          "transition-colors duration-200",
          hasWin ? WON_CARD_RING : "group-hover:border-primary/40"
        )}
      >
        {image}
        {/* Compact body: title, then hook + CTA sharing one row — no dedicated
            footer row, so two card rows fit the panel without scrolling. */}
        <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
          <Typography tag="h5" className="truncate leading-normal">
            {campaignDisplayTitle(campaign, t)}
          </Typography>
          <div className="mt-auto flex items-center justify-between gap-2">
            {hookValue != null ? (
              <HookLine
                value={hookValue}
                label={hookLabel}
                isCatalogItem={hookIsCatalogItem}
              />
            ) : (
              <span />
            )}
            <CardCta campaign={campaign} className="shrink-0" />
          </div>
        </div>
      </div>
    </CampaignLink>
  );
};
